import React, { useState, useEffect } from "react";
import secureLocalStorage from "react-secure-storage";

// Module-level blob URL cache — persists for the session, cleared on full page reload (logout).
const _blobCache = new Map();

const _getToken = () => {
  try {
    const info = secureLocalStorage.getItem("adminInfo");
    const parsed = typeof info === "string" ? JSON.parse(info) : info;
    return parsed?.authToken || "";
  } catch {
    return "";
  }
};

// Converts a /public/ URL to the authenticated backend endpoint.
// Non-/public/ URLs are returned unchanged (logos, system icons, etc.).
export const toSecureUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/public\/.+/);
  if (!match) return url;
  const filePath = match[0].substring(1); // strip leading '/' → "public/..."
  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/api\/v1\/?$/, "");
  return `${base}/api/v1/secure-file?p=${encodeURIComponent(filePath)}`;
};

/**
 * Secure image component for files stored under /public/.
 *
 * Strategy:
 *   1. Try fetching through the authenticated /api/v1/secure-file endpoint.
 *      On success → render as a blob: URL (real path hidden from DevTools).
 *   2. If the secure fetch fails for any reason (endpoint not yet deployed,
 *      network error, etc.) → render a plain <img src={originalUrl}>.
 *      Native <img> loading does not require CORS so images always show.
 *   3. If even the native load fails → render the fallback (Avatar / null).
 *
 * This means images never break regardless of backend deployment state.
 */
const AuthenticatedImage = ({ src, fallback = null, ...imgProps }) => {
  // null  = still loading
  // false = use native img (secure fetch failed)
  // string (blob: URL) = secure fetch succeeded
  const [blobUrl, setBlobUrl] = useState(() =>
    src && _blobCache.has(src) ? _blobCache.get(src) : null
  );
  const [useNative, setUseNative] = useState(false);
  const [nativeFailed, setNativeFailed] = useState(false);

  useEffect(() => {
    if (!src) return;

    // Cache hit — no refetch needed
    if (_blobCache.has(src)) {
      setBlobUrl(_blobCache.get(src));
      setUseNative(false);
      return;
    }

    const secureUrl = toSecureUrl(src);
    const token = _getToken();
    let cancelled = false;

    fetch(secureUrl, { headers: { authToken: token } })
      .then((r) => {
        if (!r.ok) throw new Error("not ok");
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        _blobCache.set(src, url);
        setBlobUrl(url);
      })
      .catch(() => {
        // Secure endpoint unavailable — fall back to native <img> loading.
        // Native img does not need CORS and works exactly as the original code did.
        if (!cancelled) setUseNative(true);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  // No src at all
  if (!src) return fallback;

  // Secure fetch succeeded — blob URL hides the real path
  if (blobUrl) {
    return <img src={blobUrl} alt="" {...imgProps} />;
  }

  // Secure fetch failed — use native img with original URL (same as pre-change behaviour)
  if (useNative) {
    if (nativeFailed) return fallback;
    return (
      <img
        src={src}
        alt=""
        {...imgProps}
        onError={() => setNativeFailed(true)}
      />
    );
  }

  // Still loading (secure fetch in flight) — show fallback (Avatar / placeholder)
  return fallback;
};

// The same secure loading for places that need an address rather than an <img> (antd Avatar, a preview):
// the blob: URL once loaded (shared cache, one fetch per image), the plain URL if the secure endpoint fails,
// null while loading or without an image.
const _inflight = new Map();
const loadSecureImage = (src) => {
  if (_blobCache.has(src)) return Promise.resolve(_blobCache.get(src));
  if (_inflight.has(src)) return _inflight.get(src);
  const pending = fetch(toSecureUrl(src), { headers: { authToken: _getToken() } })
    .then((r) => {
      if (!r.ok) throw new Error("not ok");
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      _blobCache.set(src, url);
      return url;
    })
    .finally(() => _inflight.delete(src));
  _inflight.set(src, pending);
  return pending;
};

export const useSecureImage = (src) => {
  const [url, setUrl] = useState(() => (src && _blobCache.has(src) ? _blobCache.get(src) : null));
  useEffect(() => {
    if (!src) { setUrl(null); return undefined; }
    if (_blobCache.has(src)) { setUrl(_blobCache.get(src)); return undefined; }
    let cancelled = false;
    setUrl(null);
    loadSecureImage(src)
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setUrl(src); });
    return () => { cancelled = true; };
  }, [src]);
  return url;
};

export default AuthenticatedImage;
